import { useEffect, useState } from 'react';
import { Breadcrumbs, IconButton, Tooltip } from '@mui/material';
import { Link, useLocation, useNavigate } from 'react-router';
import { ChevronRight } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import HomeIcon from '@mui/icons-material/Home';
import { useDispatch, useSelector } from 'react-redux';
import {
  resetIsDataUpdated,
  setResetDataFlag,
  setActiveWorkItem,
  setEditMode,
  setProductStatus,
  resetProductData,
} from '../store/states/procos';
import { AppDispatch, AppState } from '../store/AppStore';
import { Master, ECommonDialogType, EPageName, EFeatureStatus } from '../data/Constants';
import { resetToInitial } from '../store/states/PdmSlice';
import ConfirmationDialog from './ConfirmationDialog';
import { resetToInitialState } from '../store/states/CategorySlice';

import type { IDialogFor } from '../../types';
import type { IProduct } from '../../types/IComponentTypes';

//Home button method props type
interface IHomeButton {
  navigateToHome: () => void;
}

//return the current breadcrum list to to Breadcrum component
const getBreadCrumbs = (product: IProduct, location, t) => {
  const breadCrumbs: string[] = [];

  if (product?.name && location.pathname !== EPageName.Home) {
    breadCrumbs.push(`${product.name} (${product.id})`);
  } else {
    breadCrumbs.push(t('home.header'));
  }

  return breadCrumbs;
};

//Returns the Home Icon to the Breadcrumbar component
const HomeButton = ({ navigateToHome }: IHomeButton) => {
  return (
    <span>
      <IconButton
        onClick={() => navigateToHome()}
        className="home-icon"
        data-testid="btn-home-icon"
      >
        <HomeIcon />
      </IconButton>
    </span>
  );
};

/**
 * This component is to show the home icon and selected product and feature information.
 * @returns {JSX.Element}  Which renders the Home Icon and Product name and code
 */
const BreadcrumbBar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  const [confirmWarning, setConfirmWarning] = useState({
    open: false,
    title: 'dialogBox.unsavedChanges',
    description: 'messages.cancel.homeWarning',
    alertFor: ECommonDialogType.HomeClickWarning,
  });
  const [breadCrumbs, setBreadCrumbs] = useState<string[]>([]);

  const product = useSelector((state: AppState) => state.procos.product);
  const isDataUpdated = useSelector((state: AppState) => {
    if (
      state.procos.isDataUpdated ||
      state.pdm.isModified ||
      state.category.isCategoryDataModified
    ) {
      return true;
    } else {
      return false;
    }
  });
  const isDataReset = useSelector((state: AppState) => state.procos.isDataReset);
  const newWorkItem = useSelector((state: AppState) => state.procos.newWorkItem);
  const productStatus = useSelector((state: AppState) => state.procos.productStatus);

  //set breadcrumbs state while loading the page
  useEffect(() => setBreadCrumbs(getBreadCrumbs(product, location, t)), [t, location]);

  //Show warning message while navigate to home without saving data
  const navigateToHome = () => {
    if (isDataUpdated) {
      setConfirmWarning({ ...confirmWarning, open: true });
    } else {
      dispatch(setResetDataFlag(true));
    }
  };

  //Call resetData method after confirm the save warning message
  useEffect(() => {
    if (isDataReset && newWorkItem == null) {
      dispatch(setResetDataFlag(false));
      resetData();
    }
  }, [isDataReset]);

  //Reset the data on navigation to home
  const resetData = () => {
    if (location.pathname.includes(EPageName.Category)) {
      // Set the category state to the initial state
      dispatch(resetToInitialState());
    } else if (location.pathname.includes(EPageName.Pdm)) {
      //Pdm state reset
      dispatch(resetToInitial());
    } else {
      //Procos state reset
      resetProcosData();
      setBreadCrumbs([]);
    }

    //Reset Procos Dat when the Product is InActive
    if (productStatus !== EFeatureStatus.Active) {
      resetProcosData();
    }

    setBreadCrumbs([]);
    dispatch(resetProductData());

    navigate(EPageName.Home, { replace: true });
  };

  //Reset Procos Data when navigate to landing Page
  const resetProcosData = () => {
    dispatch(setProductStatus(EFeatureStatus.Active));
    dispatch(setActiveWorkItem(Master));
    dispatch(setEditMode({ isEdit: false }));
    dispatch(resetIsDataUpdated());
  };

  //This method triggers when user clicks on cancel or ok button on confirm dialog
  const onConfirmStatus = (confirm: IDialogFor) => {
    setConfirmWarning({ ...confirmWarning, open: false });
    if (confirm.status) {
      resetData();
    }
  };

  return (
    <>
      {location.pathname !== EPageName.Home && <HomeButton navigateToHome={navigateToHome} />}
      <Breadcrumbs className="breadCrumb root" separator={<ChevronRight />}>
        {breadCrumbs?.map((item: string, i: number) => (
          <span id={item + i} key={item + i} className="breadcrumbs-li">
            <Tooltip key={item} title={item}>
              <span>
                <Link className="item" to={item.href} replace={true}>{`${item}`}</Link>
              </span>
            </Tooltip>
          </span>
        ))}
      </Breadcrumbs>
      {confirmWarning.open && (
        <ConfirmationDialog dialogInfo={confirmWarning} onConfirmAction={onConfirmStatus} />
      )}
    </>
  );
};
export default BreadcrumbBar;
